import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { gql, useQuery, useMutation } from '@apollo/client';
import confetti from 'canvas-confetti';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Stack,
  Zoom,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import useResponsive from '../hooks/useResponsive';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckIcon from '@mui/icons-material/Check';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PlaceIcon from '@mui/icons-material/Place';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

const UNCATEGORIZED_QUERY = gql`
  query Uncategorized {
    transactions(hasCategory: false, isHidden: false, limit: 9999) {
      items {
        id
        date
        bankDescription
        place
        amount
        movementType
        category {
          id
          name
          color
        }
      }
      totalCount
    }
  }
`;

const CATEGORIES_QUERY = gql`
  query Categories {
    categories {
      id
      name
      color
    }
  }
`;

const UPDATE_TRANSACTION_CATEGORY = gql`
  mutation UpdateTransactionCategory($transactionId: Int!, $categoryId: Int) {
    updateTransactionCategory(
      transactionId: $transactionId
      categoryId: $categoryId
    ) {
      transaction {
        id
        category {
          id
          name
          color
        }
      }
    }
  }
`;

const UPDATE_TRANSACTION_HIDDEN = gql`
  mutation UpdateTransactionHidden($transactionId: Int!, $hidden: Boolean!) {
    updateTransactionHidden(transactionId: $transactionId, hidden: $hidden) {
      transaction {
        id
        hidden
      }
    }
  }
`;

const formatCLP = (amount) => {
  const num = parseFloat(amount);
  const sign = num < 0 ? '-' : '';
  const abs = Math.abs(num);
  const formatted = abs.toLocaleString('es-CL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return `${sign}$${formatted}`;
};

const formatDate = (dateStr) => {
  const [year, month, day] = dateStr.split('-');
  return `${day}/${month}/${year}`;
};

// Returns white or dark text depending on background luminance
const contrastColor = (hex) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.55 ? '#1E293B' : '#FFFFFF';
};

export default function Classify() {
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [assignments, setAssignments] = useState({}); // txId → { id, name, color }
  const [hiddenIds, setHiddenIds] = useState(new Set());
  const [savingTxId, setSavingTxId] = useState(null);
  const [slideDirection, setSlideDirection] = useState('left');
  const [justSelected, setJustSelected] = useState(null);

  const { data, loading } = useQuery(UNCATEGORIZED_QUERY, {
    fetchPolicy: 'network-only',
  });
  const { data: catData } = useQuery(CATEGORIES_QUERY);
  const [updateTransactionCategory] = useMutation(UPDATE_TRANSACTION_CATEGORY);
  const [updateTransactionHidden] = useMutation(UPDATE_TRANSACTION_HIDDEN);

  const allTransactions = data?.transactions?.items || [];
  const transactions = allTransactions.filter((t) => !hiddenIds.has(t.id));
  const categories = catData?.categories || [];
  const total = transactions.length;

  const goBack = useCallback(() => {
    setSlideDirection('right');
    setCurrentIndex((i) => Math.max(0, i - 1));
  }, []);

  const goForward = useCallback(() => {
    setSlideDirection('left');
    setCurrentIndex((i) => i + 1);
  }, []);

  const handleExit = useCallback(() => {
    navigate('/transactions');
  }, [navigate]);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowLeft') goBack();
      if (e.key === 'ArrowRight') goForward();
      if (e.key === 'Escape') handleExit();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [goBack, goForward, handleExit]);

  const handleSelectCategory = async (tx, category, event) => {
    setSavingTxId(tx.id);
    setJustSelected(category.id);
    // Confetti burst from clicked button center
    const rect = event.currentTarget.getBoundingClientRect();
    confetti({
      particleCount: 60,
      spread: 70,
      origin: {
        x: (rect.left + rect.width / 2) / window.innerWidth,
        y: (rect.top + rect.height / 2) / window.innerHeight,
      },
      colors: ['#22C55E', '#16A34A', '#3B82F6', '#FFFFFF'],
      disableForReducedMotion: true,
    });
    try {
      await updateTransactionCategory({
        variables: {
          transactionId: parseInt(tx.id),
          categoryId: parseInt(category.id),
        },
      });
      setAssignments((prev) => ({ ...prev, [tx.id]: category }));
      // Auto-advance after pulse + spring check complete
      setTimeout(() => {
        setSlideDirection('left');
        setCurrentIndex((i) => i + 1);
        setSavingTxId(null);
        setJustSelected(null);
      }, 350);
    } catch {
      setSavingTxId(null);
      setJustSelected(null);
    }
  };

  const handleHide = async (tx) => {
    setSavingTxId(tx.id);
    setSlideDirection('left');
    try {
      await updateTransactionHidden({
        variables: { transactionId: parseInt(tx.id), hidden: true },
      });
      setHiddenIds((prev) => new Set(prev).add(tx.id));
      setSavingTxId(null);
    } catch {
      setSavingTxId(null);
    }
  };

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'background.default',
        }}
      >
        <Stack alignItems="center" spacing={2}>
          <CircularProgress />
          <Typography color="text.secondary">
            Cargando transacciones...
          </Typography>
        </Stack>
      </Box>
    );
  }

  // Empty state — nothing to classify
  if (total === 0) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'background.default',
        }}
      >
        <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 4, maxWidth: 420 }}>
          <CheckCircleIcon
            sx={{ fontSize: 72, color: 'success.main', mb: 2 }}
          />
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            ¡Ya está todo clasificado!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            No hay transacciones sin categoría.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleExit}
            size="large"
          >
            Volver a transacciones
          </Button>
        </Paper>
      </Box>
    );
  }

  // Completion state — all done
  if (currentIndex >= total) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'background.default',
        }}
      >
        <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 4, maxWidth: 420 }}>
          <CheckCircleIcon
            sx={{ fontSize: 72, color: 'success.main', mb: 2 }}
          />
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            ¡Listo!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Todas las transacciones fueron clasificadas.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleExit}
            size="large"
          >
            Volver a transacciones
          </Button>
        </Paper>
      </Box>
    );
  }

  const tx = transactions[currentIndex];
  const amount = parseFloat(tx.amount);
  const assignedCategory = assignments[tx.id] || null;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'background.default',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Top bar: close button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
        <IconButton onClick={handleExit} size="large">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Progress bar */}
      <Box sx={{ px: 3, mb: 1 }}>
        {isMobile ? (
          <LinearProgress
            variant="determinate"
            value={total > 0 ? (currentIndex / total) * 100 : 0}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: 'divider',
              '& .MuiLinearProgress-bar': { backgroundColor: 'secondary.main' },
            }}
          />
        ) : (
          <Box sx={{ display: 'flex', gap: '3px' }}>
            {transactions.map((t, i) => (
              <Box
                key={t.id}
                sx={{
                  flex: 1,
                  height: i === currentIndex ? 6 : 4,
                  borderRadius: 3,
                  backgroundColor:
                    i < currentIndex || assignments[t.id]
                      ? 'secondary.main'
                      : i === currentIndex
                      ? 'primary.main'
                      : 'divider',
                  transition: 'all 0.2s ease',
                  alignSelf: 'center',
                }}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* Counter */}
      <Typography
        variant="overline"
        align="center"
        sx={{ fontWeight: 700, color: 'text.secondary', mb: 3, letterSpacing: '0.15em' }}
      >
        {currentIndex + 1} / {total}
      </Typography>

      {/* Main content area */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          px: 3,
          maxWidth: 600,
          mx: 'auto',
          width: '100%',
        }}
      >
        {/* Transaction card */}
        <Paper
          key={currentIndex}
          sx={{
            p: { xs: 2.5, sm: 4 },
            borderRadius: 4,
            textAlign: 'center',
            width: '100%',
            mb: 4,
            borderTop: '2px solid',
            borderTopColor: amount < 0 ? 'text.primary' : 'success.main',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            animation: `${slideDirection === 'left' ? 'classifySlideInRight' : 'classifySlideInLeft'} 0.3s cubic-bezier(0.16,1,0.3,1)`,
            '@keyframes classifySlideInRight': {
              from: { transform: 'translateX(40px)', opacity: 0 },
              to: { transform: 'translateX(0)', opacity: 1 },
            },
            '@keyframes classifySlideInLeft': {
              from: { transform: 'translateX(-40px)', opacity: 0 },
              to: { transform: 'translateX(0)', opacity: 1 },
            },
          }}
        >
          {/* Amount */}
          <Typography
            variant="h2"
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontWeight: 800,
              color: amount < 0 ? 'text.primary' : 'success.main',
              mb: 2,
              letterSpacing: '-0.03em',
            }}
          >
            {formatCLP(tx.amount)}
          </Typography>

            {/* Description */}
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                mb: 1.5,
                color: 'text.primary',
                lineHeight: 1.3,
              }}
            >
              {tx.bankDescription}
            </Typography>

            {/* Place */}
            {tx.place && (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                spacing={0.5}
                sx={{ mb: 1 }}
              >
                <PlaceIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="body1" color="text.secondary">
                  {tx.place}
                </Typography>
              </Stack>
            )}

            {/* Date */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={0.5}
            >
              <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {formatDate(tx.date)}
              </Typography>
            </Stack>
          </Paper>

        {/* Category selector */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.5,
            width: '100%',
            mb: 3,
          }}
        >
          {categories.map((cat) => {
            const isAssigned = assignedCategory?.id === cat.id;
            const isPulsing = justSelected === cat.id;
            return (
              <Button
                key={cat.id}
                onClick={(e) => handleSelectCategory(tx, cat, e)}
                disabled={savingTxId === tx.id}
                sx={{
                  backgroundColor: isPulsing ? 'success.main' : cat.color,
                  color: isPulsing ? '#FFFFFF' : contrastColor(cat.color),
                  py: 1.5,
                  px: 2,
                  borderRadius: 2,
                  fontWeight: 700,
                  fontSize: '0.9rem',
                  textTransform: 'none',
                  border: '2px solid',
                  borderColor: isAssigned ? 'text.primary' : 'transparent',
                  position: 'relative',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  transform: isPulsing ? 'scale(1.08)' : 'scale(1)',
                  transition: 'transform 0.25s cubic-bezier(0.34,1.56,0.64,1), background-color 0.25s ease, color 0.25s ease, border-color 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    backgroundColor: isPulsing ? 'success.main' : cat.color,
                    filter: 'brightness(0.9)',
                    transform: isPulsing ? 'scale(1.08)' : 'scale(1.04)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  },
                }}
              >
                {isAssigned && (
                  <Box component="span" sx={{ display: 'inline-flex', width: 18, mr: 0.5, flexShrink: 0, alignItems: 'center', justifyContent: 'center', verticalAlign: 'middle' }}>
                    <Zoom in={isAssigned} timeout={250} style={{ transitionDelay: '50ms' }}>
                      <CheckIcon sx={{ fontSize: 18 }} />
                    </Zoom>
                  </Box>
                )}
                {cat.name}
              </Button>
            );
          })}
        </Box>

        {/* Navigation */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          sx={{ mb: 4, width: { xs: '100%', sm: 'auto' } }}
        >
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={goBack}
            disabled={currentIndex === 0}
            fullWidth={isMobile}
          >
            Anterior
          </Button>
          <Button
            variant="outlined"
            startIcon={<VisibilityOffIcon />}
            onClick={() => handleHide(tx)}
            disabled={savingTxId === tx.id}
            fullWidth={isMobile}
          >
            Esconder
          </Button>
          <Button
            variant="outlined"
            endIcon={<ArrowForwardIcon />}
            onClick={goForward}
            fullWidth={isMobile}
          >
            Saltar
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
